import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Upload, FileText, Building, Loader2 } from "lucide-react";
import api from "@/utils/api";
import { toast } from "@/hooks/use-toast";
import {
  setCfcData,
  setCurrentTab,
  setUserInitiatedNav,
} from "../../../store/slices/cfcSlice";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import FilePreview from "../../../components/common/FilePreview";

const particularsSchema = z
  .object({
    account_holder_name: z.string().min(1, "Account Holder Name is required"),
    account_no: z.string().min(1, "Account Number is required"),
    confirm_account_no: z.string().min(1, "Confirm Account Number is required"),
    account_type: z.string().min(1, "Account Type is required"),
    account_operated_by: z.string().min(1, "Account Operated By is required"),
    bank_name: z.string().min(1, "Bank Name is required"),
    bank_address: z.string().min(1, "Bank Address is required"),
    branch: z.string().min(1, "Branch is required"),
    bank_district: z.string().min(1, "Bank District is required"),
    ifsc_code: z
      .string()
      .min(1, "IFSC Code is required")
      .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, "Invalid IFSC Code format"),
    micr_code: z
      .string()
      .min(1, "MICR Code is required")
      .regex(/^\d{9}$/, "MICR Code must be 9 digits"),
    bank_passbook: z
      .any()
      .refine(
        (file) => file instanceof File || typeof file === "string",
        "Bank Passbook is required"
      ),
  })
  .refine((data) => data.account_no === data.confirm_account_no, {
    message: "Account numbers do not match",
    path: ["confirm_account_no"],
  });

const BankDetails = ({ data, districts, communities }) => {
  const [filePreviews, setFilePreviews] = useState({
    bank_passbook: null,
  });
  const location = useLocation();
  const nav = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [buttonType, setButtonType] = useState("submit");
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    trigger,
  } = useForm({
    resolver: zodResolver(particularsSchema),
    defaultValues: {
      account_holder_name: data?.bank?.account_holder_name ?? "",
      account_no: data?.bank?.account_no ?? "",
      confirm_account_no: data?.bank?.account_no ?? "",
      account_type: data?.bank?.account_type ?? "",
      account_operated_by: data?.bank?.account_operated_by ?? "",
      bank_name: data?.bank?.bank_name ?? "",
      bank_address: data?.bank?.bank_address ?? "",
      branch: data?.bank?.branch ?? "",
      bank_district: data?.bank?.bank_district ?? "",
      ifsc_code: data?.bank?.ifsc_code ?? "",
      micr_code: data?.bank?.micr_code ?? "",
      bank_passbook: data?.bank?.bank_passbook ?? "",
    },
  });

  const formData = watch();

  useEffect(() => {
    if (data?.bank) {
      const previews = {
        bank_passbook: data?.bank?.bank_passbook ?? "",
      };
      setFilePreviews(previews);
    }
  }, [data]);

  const handleFileUpload = async (name, file) => {
    if (file) {
      if (name === "photo" && file.type === "application/pdf") {
        toast({
          title: "Invalid File Type",
          description:
            "PDF files are not allowed for photo upload. Please upload an image file.",
          variant: "destructive",
        });
        return;
      }

      setValue(name, file, { shouldValidate: true });

      if (name === "photo" && file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFilePreviews((prev) => ({ ...prev, photo: e.target.result }));
        };
        reader.readAsDataURL(file);
      }

      await trigger(name);
    }
  };

  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      const searchParams = new URLSearchParams(location?.search);
      const submitData = new FormData();

      Object.keys(formData).forEach((key) => {
        if (formData[key] !== undefined && formData[key] !== null) {
          if (formData[key] instanceof File) {
            submitData.append(key, formData[key]);
          } else {
            submitData.append(key, formData[key]);
          }
        }
      });

      submitData.append("id", data?.id || "");

      const response = await api.post("/cfc-subsidies/bank", submitData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      dispatch(setCfcData(response?.data?.data || {}));

      toast({
        title: "Success!",
        description: "Application Saved successfully",
        variant: "success",
      });

      searchParams.delete("tab");
      nav(
        {
          pathname: location.pathname,
          search: searchParams.toString(),
        },
        { replace: true }
      );

      dispatch(setUserInitiatedNav(true));
      if (buttonType === "next") {
        dispatch(setCurrentTab("priority"));
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast({
        title: "Submission Failed",
        description:
          error.response?.data?.message || "Failed to submit application",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = async (fieldName, value) => {
    setValue(fieldName, value, { shouldValidate: true });
  };

  const handleRemoveFile = (fieldName) => {
    setValue(fieldName, undefined, { shouldValidate: true });
    setFilePreviews((prev) => ({ ...prev, [fieldName]: null }));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Bank Account Details
          </CardTitle>
        </CardHeader>
        <CardDescription className="p-7">
          <p className="font-bold">Important Notes</p>
          <ul className="list-disc pt-3 px-3">
            <li className="pb-2">
              Proper Bank account details should be given
            </li>
            <li className="pb-2">
              Eligible Subsidy will be released to given bank account only
            </li>
            <li className="pb-2">
              Improper / Incorrect account details will lead to failure of
              transaction / Release of Funds to wrong Account
            </li>
            <li className="pb-2">
              The SPV is fully responsible for any variation in the bank account
              details
            </li>
            <li className="pb-2">
              The Account holder should be a member of the SPV
            </li>
          </ul>
        </CardDescription>
        <CardContent className="space-y-6 mt-5">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="account_holder_name">
                Account Holder Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="account_holder_name"
                {...register("account_holder_name")}
                onChange={(e) =>
                  handleInputChange("account_holder_name", e.target.value)
                }
                className={errors.account_holder_name ? "border-red-500" : ""}
              />
              {errors.account_holder_name && (
                <p className="text-sm text-red-500">
                  {errors.account_holder_name.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="account_no">
                Account Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="account_no"
                onPaste={(e) => e.preventDefault()}
                onCopy={(e) => e.preventDefault()}
                onDrop={(e) => e.preventDefault()}
                onKeyDown={(e) => {
                  if (
                    (e.ctrlKey || e.metaKey) &&
                    ["v", "c", "x"].includes(e.key.toLowerCase())
                  ) {
                    e.preventDefault();
                  }
                }}
                {...register("account_no")}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  handleInputChange("account_no", value);
                }}
                className={errors.account_no ? "border-red-500" : ""}
              />
              {errors.account_no && (
                <p className="text-sm text-red-500">
                  {errors.account_no.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm_account_no" required>
                Confirm Account Number
              </Label>
              <Input
                id="confirm_account_no"
                onPaste={(e) => e.preventDefault()}
                onCopy={(e) => e.preventDefault()}
                onDrop={(e) => e.preventDefault()}
                onKeyDown={(e) => {
                  if (
                    (e.ctrlKey || e.metaKey) &&
                    ["v", "c", "x"].includes(e.key.toLowerCase())
                  ) {
                    e.preventDefault();
                  }
                }}
                {...register("confirm_account_no")}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  handleInputChange("confirm_account_no", value);
                }}
                className={errors.confirm_account_no ? "border-red-500" : ""}
              />
              {errors.confirm_account_no && (
                <p className="text-sm text-red-500">
                  {errors.confirm_account_no.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="account_type">
                Account Type <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.account_type || ""}
                onValueChange={(value) =>
                  handleInputChange("account_type", value)
                }
              >
                <SelectTrigger
                  className={errors.account_type ? "border-red-500" : ""}
                >
                  <SelectValue placeholder="Select Account Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Savings Account">
                    Savings Account
                  </SelectItem>
                  <SelectItem value="Current Account">
                    Current Account
                  </SelectItem>
                </SelectContent>
              </Select>
              {errors.account_type && (
                <p className="text-sm text-red-500">
                  {errors.account_type.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="account_operated_by">
                Account Operated By Individual / Joint Account
                <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.account_operated_by || ""}
                onValueChange={(value) =>
                  handleInputChange("account_operated_by", value)
                }
              >
                <SelectTrigger
                  className={errors.account_operated_by ? "border-red-500" : ""}
                >
                  <SelectValue placeholder="Select Account Operated" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Individual">Individual</SelectItem>
                  <SelectItem value="Joint Account">Joint Account</SelectItem>
                </SelectContent>
              </Select>
              {errors.account_operated_by && (
                <p className="text-sm text-red-500">
                  {errors.account_operated_by.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="bank_name">
                Bank Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="bank_name"
                {...register("bank_name")}
                onChange={(e) => handleInputChange("bank_name", e.target.value)}
                className={errors.bank_name ? "border-red-500" : ""}
              />
              {errors.bank_name && (
                <p className="text-sm text-red-500">
                  {errors.bank_name.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="bank_address">
                Bank Address <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="bank_address"
                {...register("bank_address")}
                onChange={(e) => {
                  handleInputChange("bank_address", e.target.value);
                }}
                className={errors.bank_address ? "border-red-500" : ""}
              />
              {errors.bank_address && (
                <p className="text-sm text-red-500">
                  {errors.bank_address.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="branch">
                Branch <span className="text-red-500">*</span>
              </Label>
              <Input
                id="branch"
                {...register("branch")}
                onChange={(e) => {
                  handleInputChange("branch", e.target.value);
                }}
                className={errors.branch ? "border-red-500" : ""}
              />
              {errors.branch && (
                <p className="text-sm text-red-500">{errors.branch.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="bank_district">
                District <span className="text-red-500">*</span>
              </Label>
              <Input
                id="bank_district"
                {...register("bank_district")}
                onChange={(e) => {
                  handleInputChange("bank_district", e.target.value);
                }}
                className={errors.bank_district ? "border-red-500" : ""}
              />
              {errors.bank_district && (
                <p className="text-sm text-red-500">
                  {errors.bank_district.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="ifsc_code">
                IFSC Code <span className="text-red-500">*</span>
              </Label>
              <Input
                id="ifsc_code"
                {...register("ifsc_code")}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 11);
                  handleInputChange("ifsc_code", value);
                }}
                className={errors.ifsc_code ? "border-red-500" : ""}
              />
              {errors.ifsc_code && (
                <p className="text-sm text-red-500">
                  {errors.ifsc_code.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="micr_code">
                MICR Code <span className="text-red-500">*</span>
              </Label>
              <Input
                id="micr_code"
                {...register("micr_code")}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 9);
                  handleInputChange("micr_code", value);
                }}
                className={errors.micr_code ? "border-red-500" : ""}
              />
              {errors.micr_code && (
                <p className="text-sm text-red-500">
                  {errors.micr_code.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="bank_passbook">
                Bank Passbook Copy (1st page & Last 1 month Transaction details){" "}
                <span className="text-red-500">*</span>
              </Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <Input
                  id="bank_passbook"
                  type="file"
                  accept=".jpeg,.jpg,.png,.pdf"
                  onChange={(e) =>
                    handleFileUpload("bank_passbook", e.target.files[0])
                  }
                  className={errors.bank_passbook ? "border-red-500" : ""}
                />

                <FilePreview
                  fieldName="bank_passbook"
                  preview={filePreviews.bank_passbook}
                  file={
                    formData.bank_passbook instanceof File
                      ? formData.bank_passbook
                      : null
                  }
                  existingFile={
                    typeof filePreviews.bank_passbook === "string"
                      ? filePreviews.bank_passbook
                      : null
                  }
                  onRemove={handleRemoveFile}
                />

                {errors.bank_passbook && (
                  <p className="text-sm text-red-500">
                    {errors.bank_passbook.message}
                  </p>
                )}

                <p className="ms-1 text-[12px] text-muted-foreground">
                  Supported only JPG, JPEG, PNG, PDF Files max size 2MB
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-6 gap-2">
            <Button
              type="submit"
              disabled={isSubmitting}
              onClick={() => setButtonType("submit")}
              className="min-w-[150px]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
            <Button
              type="submit"
              onClick={() => setButtonType("next")}
              disabled={isSubmitting}
              className="min-w-[150px]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  saving...
                </>
              ) : (
                "Save and Next"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
};

export default BankDetails;
